import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import cmsClient from "src/services/cmsClient";
import { adaptUserReport } from "src/features/reporting/adapters";
import IndividualObservationTableCell from "../../components/IndividualObservationTableCell/IndividualObservationTableCell";
import GreatConversationTableCell from "../../components/GreatConversationTableCell/GreatConversationTableCell";

const columnHelper = createColumnHelper<any>();

const downloadUserReport = async (
	year: number,
	form: number,
	type: "complete" | "compliant",
	store?: number,
	area?: number
) => {
	const response = await cmsClient.get<any>(
		"/observations/reports/user-by-month/csv/",
		{ params: { year, form, store, type, area }, responseType: "blob" }
	);

	return response.data;
};

const fetchUserReport = async (
	year: number,
	form: number,
	store: number,
	area?: number
) => {
	const response = await cmsClient.get<IUserReport[]>(
		"/observations/reports/user-by-month/",
		{ params: { year, form, store, area } }
	);

	return response.data;
};

const useUserReport = (
	year: number,
	form: number,
	storeId: number,
	areaId?: number,
	compliancy?: boolean,
	observationType?: IObservationType
) => {
	const query = useQuery(
		["user-report", { year, form, storeId, areaId }],
		() => fetchUserReport(year, form, storeId, areaId ?? undefined),
		{
			select: (list) => adaptUserReport(list)
		}
	);

	const months = Array.from({ length: 12 }, (_, i) => i).map((month) =>
		dayjs().month(month).format("MMM")
	);

	const monthlyColumns = months.map((month) => {
		return columnHelper.accessor(month, {
			cell: ({ cell, row }) => {
				if (observationType === "GREAT_CONVERSATION") {
					return (
						<GreatConversationTableCell
							cellValue={cell.getValue()}
							userId={row.original.user_id}
							storeId={storeId}
						/>
					);
				}

				return (
					<IndividualObservationTableCell
						cellValue={cell.getValue()}
						userId={row.original.user_id}
					/>
				);
			},
			header: month
		});
	});

	const columns = [
		columnHelper.display({
			id: "user-name",
			cell: ({ row }) => row.original.name,
			header: "User Name"
		}),
		...monthlyColumns
	];

	const download = async () => {
		const response = await downloadUserReport(
			year,
			form,
			compliancy ? "compliant" : "complete",
			storeId
		);

		const url = window.URL.createObjectURL(new Blob([response]));

		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", "user-by-month.csv");

		document.body.appendChild(link);

		link.click();
	};

	return { columns, download, ...query };
};

export default useUserReport;
